import DeleteIcon from "@mui/icons-material/Delete";
import DeselectIcon from "@mui/icons-material/Deselect";
import DownloadIcon from "@mui/icons-material/Download";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import ListOutlinedIcon from "@mui/icons-material/ListOutlined";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { MenuItem, PopoverPosition } from "@mui/material";
import * as React from "react";

import { useAnalytics } from "@/providers";
import { RobotoStyledPopoverMenu } from "@/shared/components";
import { type FileRecord } from "@/shared/domain/files";
import {
  FileNode,
  FileSystemNode,
  isDirectory,
  isFile,
  downloadFile,
} from "@/shared/domain/files/filesystem";
import { useDeleteDirectoryMutation } from "@/shared/domain/files/hooks/useDeleteDirectoryMutation";
import { useDeleteFileMutation } from "@/shared/domain/files/hooks/useDeleteFileMutation";
import { assetSourceToIngest } from "@/shared/domain/urdf";
import { LoggerService } from "@/shared/services/LoggerService";

import { useInheritedFiles } from "./inheritedFiles";

interface ActionMenuProps {
  anchorPos: PopoverPosition | undefined;
  checked: boolean;
  detailsExpandable: boolean;
  row: FileSystemNode;
  rowIsFileType: boolean;
  fetchSignedUrl: (arg: FileNode) => Promise<string | undefined>;
  handleRenameNode: () => void;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  onClose: () => void;
  onDeleteSuccess: (row: FileSystemNode) => void;
  openContent: () => void;
  openDetails: () => void;
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  /**
   * Opens the ingest wizard, which the file browser owns one instance of.
   *
   * Optional, and absent is the whole of the release gate: the browser
   * withholds the handler when `release-3d-assets` is off rather than drilling
   * a boolean through five components. With no wizard to open, a robot
   * description behaves like any other file.
   */
  onIngestAsAsset?: (file: FileRecord) => void;
  setAnchorPos: (arg: PopoverPosition | undefined) => void;
}

export const ActionMenu: React.FC<ActionMenuProps> = ({
  anchorPos,
  checked,
  detailsExpandable,
  row,
  rowIsFileType,
  fetchSignedUrl,
  handleRenameNode,
  handleSelectRows,
  onClose,
  openContent,
  openDetails,
  onDeleteSuccess,
  handleActionConfirmation,
  onIngestAsAsset,
  setAnchorPos,
}) => {
  const deleteFileMutation = useDeleteFileMutation();
  const deleteDirectoryMutation = useDeleteDirectoryMutation();
  const { trackEvent } = useAnalytics();
  // Inherited files belong to the device type, so they are renamed and deleted
  // from the device type's page.
  const readOnly = useInheritedFiles() !== null;

  const handleClose = () => {
    setAnchorPos(undefined);
    onClose();
  };

  const onClickDownload = async () => {
    if (!isFile(row)) {
      return;
    }

    const signedUrl = await fetchSignedUrl(row);

    if (signedUrl) {
      downloadFile(row.name, signedUrl);
    }
    handleClose();
  };

  const details = detailsExpandable ? (
    <MenuItem
      key="details"
      disableRipple
      onClick={() => {
        handleClose();
        openDetails();
      }}
    >
      <ListOutlinedIcon />
      Details
    </MenuItem>
  ) : null;

  // Anything convertible — a robot description or a point cloud scan — gets the
  // Ingest item, and reroutes View below: the wizard is how any ingestable
  // source becomes viewable, so seeing one means seeing the model it converts
  // to rather than the bytes it is stored as.
  const sourceToIngest = assetSourceToIngest(row);

  // Both halves have to be present: a convertible source, and somewhere to send
  // it. Deriving one handler rather than testing the pair at each use keeps the
  // two entry points below — the Ingest item and View — from drifting apart.
  const startIngest =
    sourceToIngest && onIngestAsAsset
      ? () => {
          handleClose();
          trackEvent("IngestAsAssetClicked", { name: row.name });
          onIngestAsAsset(sourceToIngest);
        }
      : undefined;

  const ingestAsAsset = startIngest ? (
    <MenuItem key="ingest-as-asset" disableRipple onClick={startIngest}>
      <PrecisionManufacturingOutlinedIcon />
      Ingest as Asset
    </MenuItem>
  ) : null;

  const fileMenuOptions = [
    <MenuItem
      key="view"
      disableRipple
      onClick={() => {
        // Viewing an ingestable source means seeing the model it converts to,
        // not the bytes it is stored as, so View opens the same wizard rather
        // than the ordinary file viewer — unless there is no wizard to open, in
        // which case the raw file is what there is.
        if (startIngest) {
          startIngest();
          return;
        }
        handleClose();
        openContent();
      }}
    >
      <VisibilityRoundedIcon />
      View
    </MenuItem>,
    details,
    <MenuItem
      key="download"
      disableRipple
      onClick={() => {
        void onClickDownload();
      }}
    >
      <DownloadIcon />
      Download
    </MenuItem>,
    ingestAsAsset,
  ];

  return (
    <>
      <RobotoStyledPopoverMenu
        id="selection-menu"
        MenuListProps={{
          "aria-labelledby": "selection-button",
        }}
        anchorReference="anchorPosition"
        anchorPosition={anchorPos}
        open={Boolean(anchorPos)}
        onClose={handleClose}
      >
        {!readOnly && (
          <MenuItem
            disableRipple
            onClick={() => {
              trackEvent("RenameFileNodeClicked", { name: row.name });
              handleRenameNode();
              handleClose();
            }}
          >
            <DriveFileRenameOutlineIcon />
            Rename
          </MenuItem>
        )}
        {checked && (
          <MenuItem
            disableRipple
            onClick={() => {
              handleClose();
              handleSelectRows([row], false);
            }}
          >
            <DeselectIcon />
            Deselect
          </MenuItem>
        )}
        {!checked && (
          <MenuItem
            disableRipple
            onClick={() => {
              handleClose();
              handleSelectRows([row], true);
            }}
          >
            <SelectAllIcon />
            Select
          </MenuItem>
        )}

        {rowIsFileType && fileMenuOptions}

        {!readOnly && (
          <MenuItem
            disableRipple
            onClick={() => {
              handleClose();
              handleActionConfirmation(
                `Delete ${isFile(row) ? "File" : "Directory"}`,
                `Are you sure you want to delete ${row.name}?`,
                async function () {
                  try {
                    if (isDirectory(row)) {
                      await deleteDirectoryMutation.mutateAsync({
                        associationId: row.directory.association_id,
                        directoryPaths: [row.directory.relative_path],
                      });
                    } else if (isFile(row)) {
                      await deleteFileMutation.mutateAsync({
                        file: row.file,
                        resourceOwnerId: row.file.org_id,
                      });
                    }

                    onDeleteSuccess(row);
                  } catch (e) {
                    LoggerService.error("Failed to delete file", e);
                  }
                },
              );
            }}
          >
            <DeleteIcon />
            Delete
          </MenuItem>
        )}
      </RobotoStyledPopoverMenu>
    </>
  );
};
